import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createEpicorJob,
  listEpicorJobs,
  listEpicorOrders,
  subscribeToEpicorJob,
} from '../../services/epicorApi';
import { Card, StatusBadge, Button } from '../ui';
import { fmt, fmtAmt, fmtDate, orderedDisplay } from './orderFormat';

const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

const PAGE_SIZE = 50;
const ALL_COMPANIES = ['AU', 'NZ'];

const Corners = () => (
  <>
    <i className="oc-corner tl" aria-hidden="true" />
    <i className="oc-corner tr" aria-hidden="true" />
    <i className="oc-corner bl" aria-hidden="true" />
    <i className="oc-corner br" aria-hidden="true" />
  </>
);

const statusLabel = (o) => {
  if (o.void_order) return 'Void';
  if (o.order_held) return 'Held';
  if (o.open_order) return 'Open';
  return 'Closed';
};

const OrdersPage = () => {
  const navigate = useNavigate();

  const [companyFilter, setCompanyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [rowsLoading, setRowsLoading] = useState(true);

  const [companies, setCompanies] = useState(['AU', 'NZ']);
  const [syncMode, setSyncMode] = useState('incremental');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [activeJobId, setActiveJobId] = useState(null);
  const [live, setLive] = useState(null);
  const [jobs, setJobs] = useState([]);
  const unsubRef = useRef(null);

  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    setLoadError(null);
    try {
      const data = await listEpicorOrders({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        company: companyFilter || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setRows(data.orders || []);
      setTotal(data.total || 0);
    } catch (e) {
      setLoadError(e.response?.data?.detail || e.message);
    } finally {
      setRowsLoading(false);
    }
  }, [page, companyFilter, statusFilter, search]);

  const loadJobs = useCallback(async () => {
    try {
      const data = await listEpicorJobs({ limit: 15 });
      setJobs((data || []).filter((j) =>
        (j.stage_filter || []).includes('epicor_sync_sales_orders')));
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => () => {
    if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchDraft.trim().length >= 2 || searchDraft.trim().length === 0
        ? searchDraft.trim()
        : search;
      if (next !== search) {
        setSearch(next);
        setPage(0);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [searchDraft, search]);

  const toggleCompany = (c) => {
    setCompanies((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  };

  const runSync = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const { job_id } = await createEpicorJob({
        companies: companies.length ? companies : ALL_COMPANIES,
        stage: 'sales_orders',
        sync_mode: syncMode,
      });
      setActiveJobId(job_id);
      if (unsubRef.current) unsubRef.current();
      unsubRef.current = subscribeToEpicorJob(
        job_id,
        (payload) => setLive(payload),
        async () => {
          unsubRef.current = null;
          setActiveJobId(null);
          await Promise.all([loadRows(), loadJobs()]);
        },
      );
      await loadJobs();
    } catch (e) {
      setSubmitError(e.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="customers-page oc-scope">
      <header className="oc-header">
        <div>
          <h1 className="oc-title">Sales orders</h1>
          <p className="oc-blurb">
            Archival Epicor orders with shipments, invoices, and payments.
            Separate from the pricing-audit order-line window.
          </p>
        </div>
      </header>

      <Card title="Sync from Epicor" subtitle="Full or incremental archival pull">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          {ALL_COMPANIES.map((c) => (
            <label key={c} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={companies.includes(c)}
                onChange={() => toggleCompany(c)}
              />
              {c}
            </label>
          ))}
          <select value={syncMode} onChange={(e) => setSyncMode(e.target.value)}>
            <option value="incremental">Incremental</option>
            <option value="full">Full (mark-absent)</option>
          </select>
          <Button onClick={runSync} disabled={submitting || !companies.length}>
            {submitting ? 'Queuing…' : 'Queue sync'}
          </Button>
          {activeJobId && <StatusBadge status="running">Job #{activeJobId}</StatusBadge>}
        </div>
        {submitError && <p className="oc-error">{submitError}</p>}
        {live?.counters && (
          <p className="oc-hint" style={{ marginTop: 8 }}>
            fetched={fmt(live.counters.fetched)} · upserted={fmt(live.counters.upserted)} ·
            rollups={fmt(live.counters.rollups_refreshed)}
          </p>
        )}
        {jobs.length > 0 && (
          <table className="oc-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Job</th>
                <th>Status</th>
                <th>Mode</th>
                <th>Finished</th>
              </tr>
            </thead>
            <tbody>
              {jobs.slice(0, 5).map((j) => (
                <tr key={j.id}>
                  <td>#{j.id}</td>
                  <td><StatusBadge status={j.status}>{j.status}</StatusBadge></td>
                  <td>{j.payload?.sync_mode || '—'}</td>
                  <td>{fmtTime(j.finished_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="oc-frame oc-blueprint" style={{ marginTop: 24 }}>
        <Corners />
        <div className="oc-toolbar" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <select
            value={companyFilter}
            onChange={(e) => { setCompanyFilter(e.target.value); setPage(0); }}
          >
            <option value="">All companies</option>
            <option value="AU">AU</option>
            <option value="NZ">NZ</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="held">Held</option>
            <option value="void">Void</option>
          </select>
          <input
            type="search"
            placeholder="Order # or PO…"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            style={{ minWidth: 200 }}
          />
          <span className="oc-hint">{fmt(total)} orders</span>
        </div>

        {loadError && <p className="oc-error">{loadError}</p>}
        <table className="oc-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Status</th>
              <th>Ordered</th>
              <th>Invoiced</th>
              <th>Paid</th>
              <th>Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {rowsLoading && (
              <tr><td colSpan={8}>Loading…</td></tr>
            )}
            {!rowsLoading && rows.length === 0 && (
              <tr><td colSpan={8}>No orders found.</td></tr>
            )}
            {rows.map((o) => (
              <tr
                key={`${o.company}-${o.order_num}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/orders/${o.company}/${o.order_num}`)}
              >
                <td>
                  <strong>{o.company} {o.order_num}</strong>
                  {o.po_num ? <div className="oc-hint">PO {o.po_num}</div> : null}
                </td>
                <td>{fmtDate(o.order_date_effective || o.order_date)}</td>
                <td>
                  {o.cust_id || o.cust_num || '—'}
                  {o.customer_name ? <div className="oc-hint">{o.customer_name}</div> : null}
                </td>
                <td>{statusLabel(o)}</td>
                <td>{fmtAmt(orderedDisplay(o))}</td>
                <td>{fmtAmt(o.invoiced_amt)}</td>
                <td>{fmtAmt(o.paid_amt)}</td>
                <td>{fmtAmt(o.outstanding_amt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="oc-pager">
          <Button disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span>Page {page + 1} / {pages}</span>
          <Button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
